import { useState } from 'react';
import { translate } from '../../localization';

// Define the shape of the state
interface ComponentState {
  logo: React.ReactNode | null;
  title: string;
  message: string;
  cta: React.ReactNode | null;
}

// Options interface for updateScenarioNoData
export interface NoDataOptions {
  logo?: React.ReactNode | null;
  cta?: React.ReactNode | null;
  title?: string;
  message?: string;
}

// Define the return type of the custom hook
interface UseBrandMessageState {
  state: ComponentState;
  updateScenarioNoData: (options?: NoDataOptions) => void;
  updateScenarioTabLevelError: (
    logo?: React.ReactNode | null,
    cta?: React.ReactNode | null
  ) => void;
  updateScenarioTabLevelApiDown: (
    logo?: React.ReactNode | null,
    cta?: React.ReactNode | null
  ) => void;
  updateScenarioPaginationLevelError: (
    logo?: React.ReactNode | null,
    cta?: React.ReactNode | null
  ) => void;
  updateScenarioPaginationLevelApiDown: (
    logo?: React.ReactNode | null,
    cta?: React.ReactNode | null
  ) => void;
  resetToDefault: () => void;
  setState: React.Dispatch<React.SetStateAction<ComponentState>>;
}

// Custom hook
const useBrandMessageState = (): UseBrandMessageState => {
  // Initialize state
  const [state, setState] = useState<ComponentState>({
    logo: null,
    title: '',
    message: '',
    cta: null,
  });

  /**
   * Updates the state for No Data scenario
   * @param options - Configuration object for the no data message
   * @param options.logo - Optional logo component to display
   * @param options.cta - Optional call-to-action component
   * @param options.title - Optional custom title (defaults to translated 'common_no_data_available')
   * @param options.message - Optional custom message (defaults to translated 'common_this_information_may_be_unavailable_due_to_data_limitations')
   *
   * @example
   * // Use default title and message
   * updateScenarioNoData({ logo: <Icon /> });
   *
   * @example
   * // Use custom title and message
   * updateScenarioNoData({
   *   logo: <Icon />,
   *   title: 'No Claims Available',
   *   message: 'Custom message with **bold text**'
   * });
   */
  const updateScenarioNoData = (options: NoDataOptions = {}) => {
    const { logo = null, cta = null, title, message } = options;

    setState({
      ...state,
      logo,
      title: title ?? translate('common_no_data_available'),
      message:
        message ??
        translate(
          'common_this_information_may_be_unavailable_due_to_data_limitations'
        ),
      cta,
    });
  };

  // Function to update state for tab level error
  const updateScenarioTabLevelError = (
    logo: React.ReactNode | null,
    cta: React.ReactNode | null
  ) => {
    setState({
      ...state,
      logo,
      title: translate('common_something_went_wrong'),
      message: translate('common_please_refresh_the_page'),
      cta,
    });
  };

  // Function to update state for tab level api down
  // this is when multiple attempts has been made api but still failing
  const updateScenarioTabLevelApiDown = (
    logo: React.ReactNode | null,
    cta: React.ReactNode | null
  ) => {
    setState({
      ...state,
      logo,
      title: translate('common_something_went_wrong'),
      message: translate('common_technical_issue_occured'),
      cta,
    });
  };

  // Function to update state for pagination level retry
  const updateScenarioPaginationLevelError = (
    logo: React.ReactNode | null,
    cta: React.ReactNode | null
  ) => {
    setState({
      ...state,
      logo,
      title: translate('common_something_went_wrong'),
      message: translate('common_please_refresh_the_page'),
      cta,
    });
  };

  // Function to update state for pagination level api down
  const updateScenarioPaginationLevelApiDown = (
    logo: React.ReactNode | null,
    cta: React.ReactNode | null
  ) => {
    setState({
      ...state,
      logo,
      title: translate('common_something_went_wrong'),
      message: translate('common_technical_issue_occured'),
      cta,
    });
  };

  // Function to reset state to default
  const resetToDefault = () => {
    setState({
      ...state,
      logo: null,
      title: '',
      message: '',
      cta: null,
    });
  };

  // Return state and functions
  return {
    state,
    updateScenarioNoData,
    updateScenarioTabLevelError,
    updateScenarioTabLevelApiDown,
    updateScenarioPaginationLevelError,
    updateScenarioPaginationLevelApiDown,
    resetToDefault,
    setState, // Optionally expose setState for more flexibility
  };
};

export default useBrandMessageState;
